/**
 * Asset validation schemas using Zod
 * Mirrors backend business rules
 */

import { z } from 'zod';
import type { LifecycleStatus, DispositionStatus, StreetAddress, DeliveryMethod } from '../../types/api';

const lifecycleStatuses: LifecycleStatus[] = [
  'Inventoried',
  'Valued',
  'Listed for Sale',
  'Under Offer',
  'Sold',
  'Payment Pending',
  'Delivered',
  'Disposed',
  'Archived',
];

const dispositionStatuses: DispositionStatus[] = [
  'Sell',
  'Keep',
  'Give Away',
  'Donate',
  'Other',
];

const streetAddresses: StreetAddress[] = [
  '30 Light St',
  '28 Light St',
  '15 Templar',
  '30 Workshop',
  'Other',
];

const deliveryMethods: DeliveryMethod[] = [
  'Pickup',
  'Courier',
  'Australia Post',
  'Freight',
  'Other',
];

const baseAssetSchema = z.object({
  unique_identifier: z.number().int().positive({ message: 'Unique identifier must be a positive number' }),
  asset_nickname: z.string().min(1, 'Nickname is required').max(500, 'Nickname must be less than 500 characters'),
  category_id: z.number().int().positive().optional().nullable(),
  subcategory_id: z.number().int().positive().optional().nullable(),
  street_address: z.enum(streetAddresses as [string, ...string[]]).optional().nullable(),
  street_address_other: z.string().max(255).optional().nullable(),
  specific_location: z.string().max(500).optional().nullable(),
  lifecycle_status: z.enum(lifecycleStatuses as [string, ...string[]]).optional(),
  disposition_status: z.enum(dispositionStatuses as [string, ...string[]]).optional().nullable(),
  disposition_other: z.string().max(255).optional().nullable(),
  disposal_avenue: z.string().max(255).optional().nullable(),
  asking_price: z.number().min(0, 'Price must be positive').optional().nullable(),
  lowest_acceptable_price: z.number().min(0, 'Price must be positive').optional().nullable(),
  current_highest_offer: z.number().min(0, 'Price must be positive').optional().nullable(),
  final_sale_price: z.number().min(0, 'Price must be positive').optional().nullable(),
  shipping_costs: z.number().min(0, 'Shipping costs must be positive').optional().nullable(),
  delivery_method: z.enum(deliveryMethods as [string, ...string[]]).optional().nullable(),
  delivery_method_other: z.string().max(255).optional().nullable(),
  marketing_description: z.string().optional().nullable(),
  responsible_person_id: z.number().int().positive().optional().nullable(),
  share_with_external: z.boolean().optional(),
});

const priceRefinement = (data: any) => {
  if (data.lowest_acceptable_price && data.asking_price) {
    return data.lowest_acceptable_price <= data.asking_price;
  }
  return true;
};

export const createAssetSchema = baseAssetSchema.refine(priceRefinement, {
  message: 'Lowest acceptable price must be less than or equal to asking price',
  path: ['lowest_acceptable_price'],
});

export const updateAssetSchema = baseAssetSchema.omit({ unique_identifier: true }).partial().refine(priceRefinement, {
  message: 'Lowest acceptable price must be less than or equal to asking price',
  path: ['lowest_acceptable_price'],
});

export type CreateAssetFormData = z.infer<typeof createAssetSchema>;
export type UpdateAssetFormData = z.infer<typeof updateAssetSchema>;
